import React, { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
  useCollectionBinding,
  useSlotGroup,
} from '@vibesites/runtime/react';
import type { Floorplan } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { parseBedCount } from '../utils/bedroomOptions';
import { matchesFloorplanType } from '../utils/floorplanTypes';

/**
 * Floor plan cards filtered to one layout type, shared by the Studios,
 * 1 Bedroom, 2 Bedrooms, 3 Bedrooms, and Townhomes pages. Each page passes
 * its own `tokenPrefix` plus either `bedrooms` (0 = studio) or `townhomes`.
 */

interface FloorplanShowcaseProps {
  tokenPrefix?: string;
  /** Bedroom count to show (0 = studio). Ignored when `townhomes` is set. */
  bedrooms?: number;
  /** Show plans whose name, code, or description mentions townhome/townhouse. */
  townhomes?: boolean;
  /** Default message when the property has no matching plans. */
  emptyMessage?: string;
}

const ctaDef: SlotGroupDefinition = {
  label: 'View All Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'View All Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

function toNumber(raw: string | number | null | undefined): number | null {
  if (raw == null || raw === '') return null;
  const n = parseFloat(String(raw).replace(/[$,]/g, ''));
  return Number.isNaN(n) ? null : n;
}

function formatBeds(plan: Floorplan): string | null {
  const beds = parseBedCount(plan.floorplan_beds_min);
  if (beds == null) return null;
  return beds === 0 ? 'Studio' : `${beds} Bed`;
}

function formatBaths(plan: Floorplan): string | null {
  const min = toNumber(plan.floorplan_baths_min);
  const max = toNumber(plan.floorplan_baths_max);
  if (min == null) return null;
  const value = max != null && max > min ? `${min} - ${max}` : `${min}`;
  return `${value} Bath`;
}

function formatSqft(plan: Floorplan): string | null {
  const min = toNumber(plan.floorplan_sqft_min);
  const max = toNumber(plan.floorplan_sqft_max);
  if (min == null || min <= 0) return null;
  const value =
    max != null && max > min
      ? `${min.toLocaleString()} - ${max.toLocaleString()}`
      : min.toLocaleString();
  return `${value} Sq. Ft.`;
}

function formatPrice(plan: Floorplan): string {
  const min = toNumber(plan.floorplan_price_min);
  if (min == null || min <= 0) return 'Call for Pricing';
  return `Starting at $${Math.round(min).toLocaleString()}`;
}

function availableCount(plan: Floorplan): number {
  const n = toNumber(plan.floorplan_available_units);
  return n != null && n > 0 ? Math.floor(n) : 0;
}

const BED_PARAM_KEYS = ['studio', 'one', 'two', 'three', 'four', 'five'];

/** Matches the `?bedroom=` values used by FloorplansList and the Floor Plans page tabs. */
function availabilityHref(plan: Floorplan): string {
  const beds = parseBedCount(plan.floorplan_beds_min);
  const key = beds == null ? null : BED_PARAM_KEYS[beds] ?? `${beds}`;
  return key ? `/floor-plans?bedroom=${key}` : '/floor-plans';
}

function positiveOrMax(raw: string | null | undefined): number {
  const n = toNumber(raw);
  return n != null && n > 0 ? n : Number.MAX_SAFE_INTEGER;
}

/** Smallest to biggest: square footage, then beds, baths, and price as tie-breakers. */
function compareBySize(a: Floorplan, b: Floorplan): number {
  const keys = (plan: Floorplan) => [
    positiveOrMax(plan.floorplan_sqft_min),
    positiveOrMax(plan.floorplan_sqft_max),
    parseBedCount(plan.floorplan_beds_min) ?? Number.MAX_SAFE_INTEGER,
    positiveOrMax(plan.floorplan_baths_min),
    positiveOrMax(plan.floorplan_price_min),
  ];
  const ka = keys(a);
  const kb = keys(b);
  for (let i = 0; i < ka.length; i++) {
    if (ka[i] !== kb[i]) return ka[i] - kb[i];
  }
  return (a.floorplan_name || '').localeCompare(b.floorplan_name || '');
}

function FloorplanCard({ plan }: { plan: Floorplan }) {
  const name = plan.floorplan_name?.trim() || 'Floor Plan';
  const specs = [formatBeds(plan), formatBaths(plan), formatSqft(plan)].filter(Boolean) as string[];
  const available = availableCount(plan);
  const image = plan.floorplan_image_url || '';

  return (
    <a data-reveal href={availabilityHref(plan)} className="reveal-fade-up fps-card group">
      <div className="fps-card-media">
        {image ? (
          <VibesitesImage
            url={image}
            alt={`${name} floor plan`}
            className="fps-card-image"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        ) : (
          <div className="fps-card-image-placeholder" aria-hidden="true" />
        )}
        {available > 0 && (
          <span className="fps-card-badge">
            {available} Available
          </span>
        )}
      </div>
      <div className="fps-card-body">
        <h3 className="fps-card-name">{name}</h3>
        {specs.length > 0 && (
          <p className="fps-card-specs">
            {specs.map((spec, i) => (
              <React.Fragment key={spec}>
                {i > 0 && <span className="fps-card-dot" aria-hidden="true">•</span>}
                <span>{spec}</span>
              </React.Fragment>
            ))}
          </p>
        )}
        <p className="fps-card-price">{formatPrice(plan)}</p>
        <span className="fps-card-link">View Availability</span>
      </div>
    </a>
  );
}

export function FloorplanShowcase({
  tokenPrefix,
  bedrooms,
  townhomes,
  emptyMessage,
}: FloorplanShowcaseProps) {
  useRevealStyles();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  const prefix = tokenPrefix || 'floorplanshowcase';
  const emptyPath = `${prefix}.empty_message`;
  const ctaPath = `${prefix}.cta`;
  const itemsPath = `${prefix}.items`;

  const floorplans = useCollectionBinding<Floorplan>(itemsPath, { collection: 'floorplans' });
  const cta = useSlotGroup(ctaPath, ctaDef);
  const ctaUrl = cta.url || '/floor-plans';
  const ctaTarget = cta._target || '_self';

  const plans = useMemo(() => {
    const list = (floorplans ?? []).filter((plan) => matchesFloorplanType(plan, { bedrooms, townhomes }));
    return [...list].sort(compareBySize);
  }, [floorplans, bedrooms, townhomes]);

  const gridCols =
    plans.length === 1
      ? 'fps-grid fps-grid--one'
      : plans.length === 2
        ? 'fps-grid fps-grid--two'
        : 'fps-grid';

  return (
    <section className="fps-section">
      <div className="fps-container">
        <EditableCollectionBinding bindingPath={itemsPath} label="Floor Plans">
          {plans.length > 0 ? (
            <div ref={gridRef} className={gridCols}>
              {plans.map((plan, i) => (
                <FloorplanCard
                  key={plan.floorplan_id || plan.id || i}
                  plan={plan}
                />
              ))}
            </div>
          ) : (
            <RichTextContent
              path={emptyPath}
              label="No Matching Floor Plans Message"
              defaultValue={`<p>${emptyMessage || 'There are no floor plans of this type listed right now. Explore all of our floor plans or contact us for upcoming availability.'}</p>`}
              as="p"
              className="font-body text-base text-center text-[var(--vibe-color-text-muted)] max-w-xl mx-auto py-6"
            />
          )}
        </EditableCollectionBinding>

        <div className="fps-cta-wrap">
          <EditableSlotGroup groupPath={ctaPath} definition={ctaDef}>
            <a href={ctaUrl} target={ctaTarget} className="fps-cta">
              <span>{cta.label || 'View All Floor Plans'}</span>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
              </svg>
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      {/* Schema-lint anchors for static analysis — hidden at runtime */}
      <RichTextContent path="floorplanshowcase.empty_message" defaultValue="" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableSlotGroup groupPath="floorplanshowcase.cta" definition={ctaDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableCollectionBinding bindingPath="floorplanshowcase.items" label="Floor Plans" className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableCollectionBinding>

      <style>{`
        .fps-section {
          padding: 3.5rem 0 4rem;
          background: #ffffff;
        }
        @media (min-width: 768px) {
          .fps-section { padding: 5rem 0 5.5rem; }
        }
        .fps-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .fps-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .fps-container { padding: 0 2rem; } }

        .fps-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 640px) {
          .fps-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (min-width: 1024px) {
          .fps-grid { grid-template-columns: repeat(3, 1fr); gap: 1.75rem; }
        }
        .fps-grid--one { max-width: 24rem; margin: 0 auto; }
        @media (min-width: 640px) { .fps-grid--one { grid-template-columns: 1fr; } }
        @media (min-width: 1024px) { .fps-grid--one { grid-template-columns: 1fr; } }
        .fps-grid--two { max-width: 48rem; margin: 0 auto; }
        @media (min-width: 1024px) { .fps-grid--two { grid-template-columns: repeat(2, 1fr); } }

        .fps-card {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #ffffff;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
          text-decoration: none;
          color: inherit;
          transition: box-shadow 0.25s ease, border-color 0.25s ease, transform 0.25s ease;
        }
        .fps-card:hover {
          box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
          border-color: #dddde1;
          transform: translateY(-2px);
        }

        .fps-card-media {
          position: relative;
          aspect-ratio: 4 / 3;
          background: #ffffff;
          border-bottom: 1px solid #f0f0f2;
          overflow: hidden;
        }
        .fps-card-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 1rem;
          display: block;
          transition: transform 0.5s ease;
        }
        .fps-card:hover .fps-card-image { transform: scale(1.04); }
        .fps-card-image-placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #ececef 0%, #e2e3e6 50%, #ececef 100%);
        }
        .fps-card-badge {
          position: absolute;
          top: 0.75rem;
          left: 0.75rem;
          padding: 0.3rem 0.7rem;
          font-family: var(--vibe-font-body);
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-secondary);
          background: var(--vibe-color-secondary);
          border-radius: 9999px;
        }

        .fps-card-body {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
          padding: 1.25rem 1.35rem 1.5rem;
          flex: 1;
        }
        .fps-card-name {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 700;
          line-height: 1.3;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--vibe-color-text);
          margin: 0;
        }
        .fps-card-specs {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.4rem;
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }
        .fps-card-dot { opacity: 0.5; }
        .fps-card-price {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          font-weight: 700;
          color: var(--vibe-color-primary);
          margin: 0.25rem 0 0;
        }
        .fps-card-link {
          margin-top: auto;
          padding-top: 0.85rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--vibe-color-primary);
        }
        .fps-card:hover .fps-card-link { text-decoration: underline; }

        .fps-cta-wrap {
          display: flex;
          justify-content: center;
          margin-top: 2.5rem;
        }
        .fps-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.875rem 2rem;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          text-decoration: none;
          color: var(--vibe-color-text-on-secondary);
          background: linear-gradient(135deg, var(--vibe-color-secondary) 0%, color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%);
          border-radius: 9999px;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .fps-cta:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 35%, transparent);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplanShowcase',
  displayName: 'Floor Plan Showcase',
  tokens: {
    'floorplanshowcase.empty_message': {
      label: 'No Matching Floor Plans Message',
      type: 'richtext',
      defaultValue:
        '<p>There are no floor plans of this type listed right now. Explore all of our floor plans or contact us for upcoming availability.</p>',
      description: 'Shown when the property has no floor plans matching this page.',
    },
  },
  slotGroups: {
    'floorplanshowcase.cta': {
      label: 'View All Button',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
        label: { label: 'Button Text', type: 'text', defaultValue: 'View All Floor Plans' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'floorplanshowcase.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
      description:
        'Floor plans filtered to this page’s layout type (bedroom count, or townhome plans by name/code/description).',
    },
  },
};

(FloorplanShowcase as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FloorplanShowcase;
